import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { QuestionBase } from '../../../models/Models';
import { CurrencyPipe } from '@angular/common';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { IOption } from '../../../models/Models';

export const MY_FORMATS_KEY = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'ff-dynamic-form-question',
  templateUrl: './dynamic-form-question.component.html',
  styleUrls: ['./dynamic-form-question.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS_KEY },
  ],
})
export class DynamicFormQuestionComponent implements OnInit {
  private _question!: QuestionBase<string>;
  filterString: string;

  constructor(private currencyPipe: CurrencyPipe) {}
  @Input() validationMessage: boolean = false;
  @Input() form!: FormGroup;

  @Input() set question(value: QuestionBase<string>) {
    this._question = value;
  }
  get question() {
    return this._question;
  }

  get files(): File[] {
    return this.form.getRawValue()[this.question.key];
  }

  get filteredData(): Array<IOption> {
    const filter =
      typeof this.form.controls[this.question.key].value === 'string'
        ? this.form.controls[this.question.key].value.toLowerCase()
        : '';
    return this.question.options.filter((option) =>
      option.text.toLowerCase().includes(filter)
    );
  }
  /**
   * Return the selection updated each time the user check an option.
   * * @internal
   */
  @Output() selection = new EventEmitter<any>();

  get isValid() {
    return this.form.controls[this.question.key].valid;
  }
  formattedAmount;

  errorMessage = '';

  ngOnInit(): void {
    if (this.question.errorMessages.required) {
      this.errorMessage = this.question.errorMessages.required;
    }
    this.form.controls[this.question.key].statusChanges.subscribe(() => {
      this.setErrorMessage();
    });
  }

  filter = (filter: string): Array<IOption> => {
    this.filterString = filter;
    if (filter.length > 0) {
      return this.question.options.filter((option) => {
        return option.text.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
      });
    } else {
      return this.question.options.slice();
    }
  };

  inputFile(event: any): void {
    const existingFiles = this.files || [];
    const newFiles = [...existingFiles, ...event.target.files];
    this.form.patchValue({ [this.question.key]: newFiles });
    this.onChange();
  }

  deleteFile(index): void {
    const existingFiles = this.files || [];
    existingFiles.splice(index, 1);
    this.form.patchValue({ [this.question.key]: existingFiles });
    this.onChange();
  }

  onChange(): void {
    const form = this.form.getRawValue();
    this.selection.emit(form);
    this.form.patchValue(form);
  }

  //Format data type if currency boolean is true
  transformAmount(element, currency: boolean) {
    if (currency) {
      this.formattedAmount = element.target.value;

      if (!isNaN(this.formattedAmount)) {
        this.formattedAmount = this.currencyPipe.transform(
          this.formattedAmount,
          '$'
        );
      }
      element.target.value = this.formattedAmount;
    }
  }

  //Filter for decimals
  keyPressDecimal(event: KeyboardEvent, currency: boolean) {
    if (currency) {
      let value = (event.target as HTMLInputElement).value;

      let key = event.key;

      if (value.includes('.') || value == '') {
        return key in '0123456789'.split('');
      } else {
        return key in '0123456789'.split('') || key == '.';
      }
    }
  }

  //Converts input in case that it has '$' or ',' to be formatted and read by the model
  onFocusCost(event, currency: boolean) {
    if (currency) {
      let val = event.target.value as string;

      val = val.split(',').join('');

      if (event.target.value.charAt(0) == '$') {
        val = val.substr(1);
      }

      event.target.value = val;
    }
  }

  // Return true if valid char for current date value with MM/DD/YYYY format
  keyPressDate(event: KeyboardEvent) {
    let value = (event.target as HTMLInputElement).value;

    let key = event.key;

    return /^((\d{0,2})|(\d{1,2}(\/\d{0,2})?)|(\d{1,2}\/\d{1,2}(\/\d{0,4})?))$/.test(
      value + key
    );
  }

  setErrorMessage(): void {
    this.errorMessage = '';
    if (this.form.controls[this.question.key].errors) {
      const firstError = Object.keys(
        this.form.controls[this.question.key].errors
      ).find((key) => this.question.errorMessages?.[key]);
      this.errorMessage = this.question.errorMessages[firstError] || '';
    }
  }

  onAutocompleteOptionSelected(event: MatAutocompleteSelectedEvent): void {
    this.form.patchValue({ [this.question.key]: event.option.value });
    this.onChange();
  }

  displayFn(option: IOption): string {
    return option && option.text ? option.text : '';
  }

  ffDatepickerHeaderKeyboard = FFDatepickerHeaderKeyboard;
}

/** Custom header component for datepicker. */
@Component({
  selector: 'ff-datepicker-header-keyboard',
  styles: [
    `
      .dp-header {
        padding: 0.5em;
        background-color: #c81414;
        color: white;
        height: 4em;
        border-radius: 4px;
      }

      .dp-horizontal {
        width: 80px;
        height: 175px;
      }

      .year-header-label {
        font-weight: 100;
        text-align: center;
        padding: 14px;
        opacity: 0.7;
      }

      .dp-year {
        margin-bottom: -9px;
        margin-top: 3px;
      }

      .month-header-label {
        font-weight: 400;
        text-align: center;
        padding: 14px;
        font-size: 1.2em;
      }

      .selector-container {
        justify-content: space-between;
        background-color: #c81414;
        border-radius: 5px;
      }

      .button-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 96%;
        background-color: white;
        padding: 15px 5px 15px;
      }

      .btn-selector {
        background-color: white;
        color: black;
        width: 10%;
        border-style: none;
        display: flex;
        margin-right: 6%;
      }

      .btn-selector-double-arrow {
        background-color: white;
        color: black;
        width: 10%;
        border-style: none;
        display: flex;
        margin-top: 10px;
      }

      .btn-selector-double-arrow .mat-icon {
        margin: -85%;
      }

      .label-header {
        margin-top: 2px;
        font-size: 14px;
      }

      .btn-selector:hover,
      .btn-selector-double-arrow:hover {
        transform: scale(1.3, 1.3);
      }
    `,
  ],
  templateUrl: '../../datepicker/headers/datepicker-header.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FFDatepickerHeaderKeyboard<D> implements OnDestroy {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>
  ) {}

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  //Gets year label to use it in the header
  get yearLabel() {
    return this._dateAdapter.format(this._calendar.activeDate, 'YYYY');
  }

  //Gets month label to use it in the header
  get monthLabel() {
    return this._dateAdapter.format(this._calendar.activeDate, 'MMMM');
  }

  get dateLabel() {
    return this._dateAdapter.format(this._calendar.activeDate, 'ddd, MMM D');
  }

  previousClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  nextClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }
}
